import { useCallback } from 'react'
import { useR2DispatchContext, useR2StateContext } from '../providers/r2Provider/R2Context'

/**
 * How long to wait before clearing, once the pointer leaves something. Only has to outlast the
 * gap between a leave and the enter of whatever the pointer landed on, so it stays well under
 * the point where a deliberate exit would look sluggish.
 */
const CLEAR_DELAY_MS = 150

// A leave fires around the enter of whatever the pointer moved onto: the next row, or the caret
// button, which zion-ui renders as a sibling of the row's inner container rather than a child,
// so moving onto it really does leave the row. Clearing straight away therefore wiped the
// outline that the enter had just staged - and between two people in one household the record
// ids match, so the enter was a no-op and only the clear landed. Deferring lets that enter
// cancel the clear, leaving only a real exit from the list to go through. Kept at module scope
// because the enter that cancels comes from a different PersonDisplay instance than the leave.
let pendingClear

function cancelPendingClear() {
  if (!pendingClear) return
  clearTimeout(pendingClear)
  pendingClear = undefined
}

/**
 * Tracks which record the pointer is over, for the record outline highlight.
 *
 * @returns {{hoverRecord: (recordId: string) => void, unhoverRecord: () => void}} hover handlers
 */
export default function useHoveredRecordOutline() {
  const r2Dispatch = useR2DispatchContext()
  const { highlightedRecordId } = useR2StateContext()

  const hoverRecord = useCallback(
    (recordId) => {
      cancelPendingClear()
      // SET_STATE spreads into a new object, so skip no-ops and spare every R2 consumer a render.
      if (recordId === highlightedRecordId) return
      r2Dispatch({ type: 'SET_STATE', value: { highlightedRecordId: recordId } })
    },
    [r2Dispatch, highlightedRecordId]
  )

  const unhoverRecord = useCallback(() => {
    cancelPendingClear()
    if (!highlightedRecordId) return
    pendingClear = setTimeout(() => {
      pendingClear = undefined
      r2Dispatch({ type: 'SET_STATE', value: { highlightedRecordId: '' } })
    }, CLEAR_DELAY_MS)
  }, [r2Dispatch, highlightedRecordId])

  return { hoverRecord, unhoverRecord }
}
